import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { NotificationContext } from '../../context/NotificationContext';
import api, { setAuth0Methods } from '../../api';
import ProviderManager from '../../components/config/ProviderManager';
import RequestTypeManager from '../../components/config/RequestTypeManager';
import LocationManager from '../../components/config/LocationManager';
import ApiKeyManager from '../../components/config/ApiKeyManager';
import UserManager from '../../components/config/UserManager';
import RequestContentManager from '../../components/config/RequestContentManager';
import InteractionCategoryManager from '../../components/config/InteractionCategoryManager';
import FormManager from '../../components/config/FormManager';

const EditConfigPage = () => {
  const { configId } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);
  const { user, logout, getAccessTokenSilently } = useAuth0();

  // Set auth methods for API calls
  useEffect(() => {
    setAuth0Methods({
      getAccessTokenSilently,
      loginWithRedirect: () => window.location.href = '/login'
    });
  }, [getAccessTokenSilently]);
  const [config, setConfig] = useState(null);
  const [clientTypes, setClientTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [showDeployModal, setShowDeployModal] = useState(false);

  useEffect(() => {
    loadConfiguration();
    loadClientTypes();
  }, [configId]);

  const loadConfiguration = async () => {
    try {
      const response = await api.get(`/admin/configurations/${configId}`);
      setConfig(response.data);
    } catch (error) {
      showNotification('Error loading configuration: ' + error.message, true);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadClientTypes = async () => {
    try {
      const response = await api.get('/enum/client-type/');
      setClientTypes(response.data);
    } catch (error) {
      showNotification('Error loading client types: ' + error.message, true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUpdateConfiguration = async () => {
    setUpdating(true);
    try {
      const updatedConfig = {
        name: config.name,
        client_type_id: config.client_type_id ? parseInt(config.client_type_id) : null,
        elevenlabs_model: config.elevenlabs_model || null,
        elevenlabs_voice_id: config.elevenlabs_voice_id || null,
        client_internal_id: config.client_internal_id || null,
        twilio_phone_number: config.twilio_phone_number || null,
        twilio_phone_number_sid: config.twilio_phone_number_sid || null,
        about_us: config.about_us || null,
        services: config.services || null,
        opening_line: config.opening_line || null,
        prompt: config.prompt || null,
        integrated: config.integrated || false
      };

      await api.put(`/admin/configurations/${configId}`, updatedConfig);
      showNotification('Configuration updated successfully!');
    } catch (error) {
      showNotification('Failed to update configuration: ' + error.message, true);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      // Send the complete configuration data to the backend
      const deploymentData = {
        client_id: config.client_id,                           // UUID for load balancer paths
        twilio_phone_number_sid: config.twilio_phone_number_sid, // Twilio SID for webhook updates
        id: config.id,                                         // Database ID (fallback)
        name: config.name,                                     // Client name
        client_internal_id: config.client_internal_id,        // Internal ID
        elevenlabs_model: config.elevenlabs_model,            // ElevenLabs model
        elevenlabs_voice_id: config.elevenlabs_voice_id,      // ElevenLabs voice
        twilio_phone_number: config.twilio_phone_number,      // Phone number (for reference)
        client_type_id: config.client_type_id                 // Client type
      };
      
      // Call the JavaScript deployment service directly (not through Python API proxy)
      const response = await fetch(`/api/deploy/${configId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(deploymentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      
      setDeployResult(result);
      setShowDeployModal(true);
      showNotification('Configuration deployed successfully!');
    } catch (error) {
      showNotification('Failed to trigger deployment: ' + error.message, true);
    } finally {
      setDeploying(false);
    }
  };

  const closeDeployModal = () => {
    setShowDeployModal(false);
    setDeployResult(null);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Configuration not found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Loading Overlay for Deployment */}
      {deploying && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <svg className="deploy-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="spinner-path" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="spinner-fill" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-white text-lg">Deploying...</p>
          </div>
        </div>
      )}

      {/* Deploy Result Modal */}
      {showDeployModal && deployResult && (
        <div className="modal-overlay" onClick={closeDeployModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {deployResult.success ? '✅ Deployment Successful' : '❌ Deployment Failed'}
              </h3>
              <button className="modal-close" onClick={closeDeployModal}>×</button>
            </div>
            <div className="modal-body">
              {deployResult.deploymentLogs && deployResult.deploymentLogs.length > 0 ? (
                <div className="deployment-logs">
                  <h4 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '16px' }}>
                    📋 Deployment Summary
                  </h4>
                  <div className="logs-container" style={{ 
                    backgroundColor: '#f8f9fa', 
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '16px',
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    {deployResult.deploymentLogs.map((log, index) => (
                      <div 
                        key={index} 
                        style={{ 
                          margin: '4px 0',
                          color: log.startsWith('✅') ? '#28a745' : 
                                 log.startsWith('📞') ? '#007bff' :
                                 log.startsWith('ℹ️') ? '#6c757d' :
                                 log.startsWith('🌐') ? '#17a2b8' :
                                 log.startsWith('   https://') ? '#6f42c1' :
                                 '#333',
                          fontWeight: log.startsWith('✅') || log.startsWith('❌') ? 'bold' : 'normal'
                        }}
                      >
                        {log}
                      </div>
                    ))}
                  </div>
                  
                  {/* Additional Configuration Info */}
                  {deployResult.loadBalancerConfig && (
                    <div style={{ marginTop: '20px' }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>
                        🔗 Load Balancer Configuration
                      </h4>
                      <div style={{ 
                        backgroundColor: '#e7f3ff', 
                        border: '1px solid #b3d7ff',
                        borderRadius: '6px',
                        padding: '12px',
                        fontSize: '13px'
                      }}>
                        <div><strong>URL Prefix:</strong> {deployResult.loadBalancerConfig.urlPrefix}</div>
                        <div style={{ marginTop: '8px' }}>
                          <strong>Accessible URLs:</strong>
                          {deployResult.loadBalancerConfig.fullUrls.map((url, index) => (
                            <div key={index} style={{ 
                              marginLeft: '16px', 
                              color: '#0066cc',
                              fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                            }}>
                              {url}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ 
                    backgroundColor: '#d4edda', 
                    border: '1px solid #c3e6cb',
                    borderRadius: '6px',
                    padding: '16px',
                    marginBottom: '16px'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#155724', fontSize: '16px' }}>
                      ✅ Deployment Status
                    </h4>
                    <p style={{ margin: '0', color: '#155724' }}>
                      {deployResult.message || 'Deployment completed successfully!'}
                    </p>
                  </div>
                  
                  {/* Show load balancer info if available */}
                  {deployResult.loadBalancerConfig && (
                    <div style={{ 
                      backgroundColor: '#e7f3ff', 
                      border: '1px solid #b3d7ff',
                      borderRadius: '6px',
                      padding: '16px',
                      marginBottom: '16px'
                    }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#0066cc', fontSize: '16px' }}>
                        🌐 Your Application URLs
                      </h4>
                      {deployResult.loadBalancerConfig.fullUrls && deployResult.loadBalancerConfig.fullUrls.map((url, index) => (
                        <div key={index} style={{ 
                          margin: '8px 0',
                          padding: '8px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '4px',
                          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                          fontSize: '13px',
                          color: '#0066cc',
                          wordBreak: 'break-all'
                        }}>
                          {url}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Show deployment results summary */}
                  {deployResult.results && (
                    <div style={{ 
                      backgroundColor: '#f8f9fa', 
                      border: '1px solid #e9ecef',
                      borderRadius: '6px',
                      padding: '16px'
                    }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#333', fontSize: '16px' }}>
                        📋 Deployment Summary
                      </h4>
                      
                      {/* URL Map Results */}
                      {deployResult.results.urlMap && (
                        <div style={{ marginBottom: '12px' }}>
                          {deployResult.results.urlMap.pathsAdded > 0 ? (
                            <div style={{ color: '#28a745' }}>
                              ✅ Added {deployResult.results.urlMap.pathsAdded} new path(s) to load balancer
                            </div>
                          ) : (
                            <div style={{ color: '#6c757d' }}>
                              ℹ️ Load balancer paths already configured
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Twilio Results */}
                      {deployResult.results.twilio && (
                        <div style={{ marginBottom: '12px' }}>
                          {deployResult.results.twilio.updated ? (
                            <div style={{ color: '#28a745' }}>
                              ✅ Twilio webhooks updated successfully
                            </div>
                          ) : deployResult.results.twilio.skipped ? (
                            <div style={{ color: '#6c757d' }}>
                              ℹ️ Twilio webhook update skipped
                            </div>
                          ) : (
                            <div style={{ color: '#6c757d' }}>
                              ℹ️ Twilio webhooks already configured correctly
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {deployResult.error && (
                    <div style={{ 
                      backgroundColor: '#f8d7da', 
                      color: '#721c24',
                      padding: '12px',
                      borderRadius: '6px',
                      marginTop: '12px',
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      fontSize: '13px'
                    }}>
                      {deployResult.error}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="modal-close-btn" 
                onClick={closeDeployModal}
                style={{
                  backgroundColor: deployResult.success ? '#28a745' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {deployResult.success ? '✅ Close' : '❌ Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back to Configurations
        </button>
        <h1>Edit Configuration: {config.name}</h1>
        <button
          onClick={handleDeploy}
          disabled={deploying}
          className="deploy-button"
        >
          {deploying ? 'Deploying...' : 'Deploy'}
        </button>
      </div>

      <div className="page-content">
        {/* Configuration Details Section */}
        <div className="section-card">
          <div className="section-header">
            <h2>Configuration Details</h2>
          </div>
          <div className="section-content">
            <form className="config-form">
              <div className="form-grid">
                <div className="input-group">
                  <label className="form-label">Client Name</label>
                  <input
                    type="text"
                    name="name"
                    value={config.name || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="Enter Client Name"
                  />
                </div>

                <div className="input-group">
                  <label className="form-label">Deployment ID (UUID)</label>
                  <input
                    type="text"
                    value={config.client_id || ''}
                    className="form-input readonly-input"
                    readOnly
                    placeholder="Auto-generated UUID"
                  />
                </div>

                <div className="input-group">
                  <label className="form-label">Client Type</label>
                  <select
                    name="client_type_id"
                    value={config.client_type_id || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    <option value="">Select a client type</option>
                    {clientTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>

                <div className="input-group">
                  <label className="form-label">ElevenLabs Model</label>
                  <input
                    type="text"
                    name="elevenlabs_model"
                    value={config.elevenlabs_model || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter Model"
                  />
                </div>

                <div className="input-group">
                  <label className="form-label">ElevenLabs Voice ID</label>
                  <input
                    type="text"
                    name="elevenlabs_voice_id"
                    value={config.elevenlabs_voice_id || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter Voice ID"
                  />
                </div>

                <div className="input-group">
                  <label className="form-label">Client Internal ID</label>
                  <input
                    type="text"
                    name="client_internal_id"
                    value={config.client_internal_id || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter Internal ID"
                  />
                </div>

                <div className="input-group">
                  <label className="form-label">Twilio Phone Number</label>
                  <input
                    type="text"
                    name="twilio_phone_number"
                    value={config.twilio_phone_number || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter Twilio Phone Number"
                  />
                </div>

                <div className="input-group">
                  <label className="form-label">Twilio Phone Number SID</label>
                  <input
                    type="text"
                    name="twilio_phone_number_sid"
                    value={config.twilio_phone_number_sid || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter Twilio Phone Number SID"
                  />
                </div>

                <div className="input-group full-width">
                  <label className="form-label">About Us</label>
                  <textarea
                    name="about_us"
                    value={config.about_us || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    rows="4"
                    placeholder="Enter About Us"
                  />
                </div>

                <div className="input-group full-width">
                  <label className="form-label">Services</label>
                  <textarea
                    name="services"
                    value={config.services || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    rows="4"
                    placeholder="Enter Services"
                  />
                </div>

                <div className="input-group full-width">
                  <label className="form-label">Opening Line</label>
                  <textarea
                    name="opening_line"
                    value={config.opening_line || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    rows="3"
                    placeholder="Enter Opening Line for AI (e.g., Hi, I'm calling from...)"
                  />
                </div>

                <div className="input-group full-width">
                  <label className="form-label">Prompt</label>
                  <textarea
                    name="prompt"
                    value={config.prompt || ''}
                    onChange={handleInputChange}
                    className="form-input"
                    rows="8"
                    placeholder="Enter the main prompt for the AI. You can use placeholders like {{SERVICE_NAME}}, {{LOCATION_NAME}}, etc."
                  />
                </div>

                <div className="input-group full-width">
                  <label className="form-label checkbox-label">
                    <input
                      type="checkbox"
                      name="integrated"
                      checked={config.integrated || false}
                      onChange={handleInputChange}
                      className="form-checkbox"
                    />
                    <span>Integrated</span>
                  </label>
                  <p className="help-text">Enable integration features for this client.</p>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* All Manager Sections */}
        <ProviderManager configId={configId} />
        <RequestTypeManager configId={configId} />
        <LocationManager configId={configId} />
        <ApiKeyManager configId={configId} />
        <UserManager configId={configId} />
        <RequestContentManager configId={configId} />
        <InteractionCategoryManager configId={configId} />
        <FormManager configId={configId} />

        {/* Update Configuration Button */}
        <div className="fixed-bottom-action">
          <div className="action-container">
            <button
              onClick={handleUpdateConfiguration}
              disabled={updating}
              className="update-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{updating ? 'Updating...' : 'Update Configuration'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditConfigPage; 